import { useCallback, useRef, useState } from 'react';
import type { Plan } from './types';

const LIMIT = 150;
const MERGE_WINDOW_MS = 1200;

type HistoryState = { past: Plan[]; present: Plan; future: Plan[] };

export type PlanGuard = (current: Plan, next: Plan) => boolean;

export const usePlanHistory = (initial: () => Plan, guard?: PlanGuard) => {
    const [state, setState] = useState<HistoryState>(() => ({ past: [], present: initial(), future: [] }));
    const lastMerge = useRef<{ key: string; at: number } | null>(null);
    const presentRef = useRef(state.present);
    const guardRef = useRef(guard);
    presentRef.current = state.present;
    guardRef.current = guard;

    const commit = useCallback((updater: (plan: Plan) => Plan, mergeKey?: string) => {
        const preview = updater(presentRef.current);
        if (preview === presentRef.current || (guardRef.current && !guardRef.current(presentRef.current, preview))) return;
        presentRef.current = preview;
        setState(prev => {
            const next = updater(prev.present);
            if (next === prev.present) return prev;
            const now = Date.now();
            const merge = mergeKey && lastMerge.current?.key === mergeKey && now - lastMerge.current.at < MERGE_WINDOW_MS;
            lastMerge.current = mergeKey ? { key: mergeKey, at: now } : null;
            if (merge) return { ...prev, present: next, future: [] };
            return { past: [...prev.past, prev.present].slice(-LIMIT), present: next, future: [] };
        });
    }, []);

    const undo = useCallback(() => {
        lastMerge.current = null;
        setState(prev => (prev.past.length
            ? { past: prev.past.slice(0, -1), present: prev.past[prev.past.length - 1], future: [prev.present, ...prev.future] }
            : prev));
    }, []);

    const redo = useCallback(() => {
        lastMerge.current = null;
        setState(prev => (prev.future.length
            ? { past: [...prev.past, prev.present], present: prev.future[0], future: prev.future.slice(1) }
            : prev));
    }, []);

    const reset = useCallback((plan: Plan) => {
        lastMerge.current = null;
        setState({ past: [], present: plan, future: [] });
    }, []);

    return {
        plan: state.present,
        commit,
        undo,
        redo,
        reset,
        canUndo: state.past.length > 0,
        canRedo: state.future.length > 0,
    };
};
